import { isEmpty, isEqual, isNil, isNumber, toNumber } from 'lodash';

import { SplitInfoFormValues } from './SplitInfo';
import {
  SplitTypeEnum,
  AnticipatedScheduleTypeEnum,
  TimePointEnum,
  AssignmentContactPersonEditModel,
  AssignmentSupervisorInfoEditModel,
  AssignmentTimesheetApproverInfoEditModel,
  AssignmentTimesheetBackupApproverInfoEditModel,
  LocationModel,
  AssignmentSplitInfoEditModel,
  AssignmentPartnerDraftModel,
  AssignmentInfoEditModel,
  AssignmentAnticipatedScheduleEditModel,
  AssignmentContractorCompanyInfoEditModel,
  AssignmentWorkerInfoEditModel,
  AssignmentTypeEnum,
  AssignmentTeammateSelectModel,
  AssignmentTeammateTypeEnum,
  AssignmentTeammatesEditModel,
  MultiRateSchedulerEditModel,
  AssignmentFinancialSchedulerEditModel,
  AssignmentBillingContactEditModel,
  OvertimeEnum,
} from 'api/backofficeApiClient';
import {
  CustomerInfoFormValues,
  isPrimaryContactSectionRequired,
} from './CustomerInfo';
import { isAddWorkerSelected } from './WorkerSection';
import { AssignmentInfoFormValues } from './AssignmentInfo';
import { WorksiteLocationFormValues } from './WorksiteLocation';
import { ContactInfoFormValues, isOtherOptionSelected } from './ContactInfo';
import { isAddCompanySelected } from './CompanySection';
import { getDateWithoutTime } from 'utils/dateUtils';
import { WorkerInfoFormValues } from './SCAssignments/WorkerInfo';
import { PartnerSplitEnum } from 'utils/assignmentUtils';
import {
  FinancialSchedulerFormValues,
  FinancialInfoSchedulerModel,
} from '../FinancialInfoSchedulerModal';
import { convertArraysToOneLength } from 'utils/arrayUtils';
import { AssignmentInitialValues } from './AssignmentModal';
import { FinancialInfoFormValues } from './FinancialInfo';
import { BillingContactFormValues } from './CustomerInfo/BillingContactSection';
import { ExtraItemEnum } from 'enums';
import { isEditorContentEmpty } from 'utils/editorUtils';

interface SplitInfoRequest {
  splitInfo?: AssignmentSplitInfoEditModel;
  partnerDraft?: AssignmentPartnerDraftModel;
}

export const mapSplitFormValuesToRequest = (
  values: SplitInfoFormValues,
): SplitInfoRequest => {
  const { splitType, existingPartnerData, newPartnerData } = values;

  let result: SplitInfoRequest = {};

  switch (splitType) {
    case SplitTypeEnum.ExistingPartner:
      result = {
        splitInfo: {
          splitType,
          partnerId: existingPartnerData?.partner,
          // Pass splitAmount if PaymentPerHour is selected
          // otherwise - pass splitPercent
          splitAmount:
            existingPartnerData?.splitLabel ===
              PartnerSplitEnum.PaymentPerHour &&
            !isNil(existingPartnerData?.splitAmount)
              ? existingPartnerData?.splitAmount
              : undefined,
          splitPercent:
            existingPartnerData?.splitLabel === PartnerSplitEnum.SplitPercent &&
            !isNil(existingPartnerData?.splitAmount)
              ? existingPartnerData?.splitAmount
              : undefined,
        },
      };
      break;

    case SplitTypeEnum.NewPartner:
      result = {
        splitInfo: {
          splitType,
          // Pass splitAmount if PaymentPerHour is selected
          // otherwise - pass splitPercent
          splitAmount:
            newPartnerData?.splitLabel === PartnerSplitEnum.PaymentPerHour &&
            !isNil(newPartnerData?.splitAmount)
              ? newPartnerData?.splitAmount
              : undefined,
          splitPercent:
            newPartnerData?.splitLabel === PartnerSplitEnum.SplitPercent &&
            !isNil(newPartnerData?.splitAmount)
              ? newPartnerData?.splitAmount
              : undefined,
        },
        partnerDraft: {
          companyName: newPartnerData?.companyName.trim(),
          subcontractorRate: !isNil(newPartnerData?.subcontractorRate)
            ? newPartnerData?.subcontractorRate
            : undefined,
          countryId: newPartnerData?.country,
          stateId: toNumber(newPartnerData?.state),
          internationalStateName:
            newPartnerData?.internationalStateName || undefined,
          internationalCityName:
            newPartnerData?.internationalCityName || undefined,
          city: newPartnerData?.city,
          address: newPartnerData?.address,
          zipCode: newPartnerData?.zip || undefined,
          firstName: newPartnerData?.firstName,
          lastName: newPartnerData?.lastName,
          email: newPartnerData?.email,
          phoneNumber: newPartnerData?.phone,
        },
      };
      break;
    case SplitTypeEnum.NoSplit:
      result = {
        splitInfo: {
          splitType,
        },
      };
      break;

    default:
      break;
  }

  return result;
};

export const mapAssignmentInfoToRequest = (
  values: AssignmentInfoFormValues,
): AssignmentInfoEditModel => {
  const {
    startDate,
    endDate,
    jobTitle,
    jobDescription,
    jobCategory,
    employmentType,
    anticipateSchedule,
    anticipateOtherData,
    preScreeningRequired,
    preScreeningBilledTo,
    workFromHome,
    specialInstructions,
  } = values;

  let anticipatedSchedule: AssignmentAnticipatedScheduleEditModel | undefined;

  if (anticipateSchedule === AnticipatedScheduleTypeEnum.Other) {
    anticipatedSchedule = {
      fromTime: anticipateOtherData?.hoursFrom as TimePointEnum,
      fromAMPM: anticipateOtherData?.dayTimeFrom,
      toTime: anticipateOtherData?.hoursTo as TimePointEnum,
      toAMPM: anticipateOtherData?.dayTimeTo,
    };
  }

  return {
    jobCategory: jobCategory || undefined,
    employmentType: employmentType || undefined,
    jobTitle,
    jobDescription: !isEditorContentEmpty(jobDescription.content)
      ? jobDescription.content
      : undefined,
    startDate: startDate ? getDateWithoutTime(startDate) : undefined,
    endDate: endDate ? getDateWithoutTime(endDate) : undefined,
    anticipatedScheduleType: isNumber(anticipateSchedule)
      ? anticipateSchedule
      : undefined,
    anticipatedSchedule,
    workFromHome,
    preScreeningRequired,
    preScreeningBilledTo: preScreeningBilledTo || undefined,
    specialInstructions: specialInstructions || undefined,
  };
};

interface CustomerInfoRequest {
  bestPrimaryContacts: AssignmentContactPersonEditModel[];
  supervisorInfo: AssignmentSupervisorInfoEditModel;
  timesheetApproverNotApplicable?: boolean;
  timesheetApproverSameAsSupervisor?: boolean;
  customerId: string;
  timesheetApproverInfo?: AssignmentTimesheetApproverInfoEditModel;
  timesheetBackupApproverInfo: AssignmentTimesheetBackupApproverInfoEditModel;
  additionalTimesheetApprovers?: AssignmentTimesheetApproverInfoEditModel[];
  billingContacts: AssignmentBillingContactEditModel[];
}

export const mapCustomerInfoFormToRequest = (
  values: CustomerInfoFormValues,
): CustomerInfoRequest => {
  const {
    generalInfo: {
      customer,
      bestContacts: bestContactIds,
      primaryContacts,
      billingContacts: billingContactIds,
      newBillingContacts,
    },
    supervisorInfo,
    timesheetApproverInfo,
    timeBackupApprover,
  } = values;

  const mappedNewBillingContacts = newBillingContacts.map((billingContact) => ({
    ...billingContact,
    contactId: undefined,
  }));

  const mappedBillingContacts: BillingContactFormValues[] = billingContactIds
    .filter(
      (billingContact) => billingContact !== ExtraItemEnum.AddBillingContact,
    )
    .map((billingContact) => {
      return {
        contactId: billingContact || undefined,
      };
    })
    .concat(mappedNewBillingContacts);

  const bestPrimaryContacts: AssignmentContactPersonEditModel[] = bestContactIds
    .filter((contactId) => !isOtherOptionSelected(contactId))
    .map((contactId) => ({
      contactId,
    }));

  if (isPrimaryContactSectionRequired(bestContactIds)) {
    bestPrimaryContacts.push({
      firstName: primaryContacts?.firstName || undefined,
      lastName: primaryContacts?.lastName || undefined,
      email: primaryContacts?.email || undefined,
      title: primaryContacts?.title || undefined,
      phoneNumber: primaryContacts?.phone || undefined,
    });
  }

  let timesheetApprover;
  let extraTimesheetApprovers: AssignmentTimesheetApproverInfoEditModel[] = [];

  if (
    !timesheetApproverInfo?.notApplicable &&
    !timesheetApproverInfo?.sameAsSupervisor
  ) {
    const [firstTimesheetApprover] = timesheetApproverInfo.timesheetApprovers;

    timesheetApprover = {
      firstName: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.firstName
        : firstTimesheetApprover.firstName || undefined,
      lastName: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.lastName
        : firstTimesheetApprover.lastName || undefined,
      email: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo.email
        : firstTimesheetApprover.email || undefined,
      phoneNumber: timesheetApproverInfo.sameAsSupervisor
        ? supervisorInfo?.phone
        : firstTimesheetApprover?.phone || undefined,
    };
  }

  if (
    !timesheetApproverInfo?.notApplicable &&
    !!timesheetApproverInfo.timesheetApprovers.length
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [firstTimesheetApprover, ...restTimesheetApprovers] =
      timesheetApproverInfo.timesheetApprovers;

    extraTimesheetApprovers = restTimesheetApprovers.map(
      (timesheetApprover) => ({
        firstName: timesheetApprover?.firstName || undefined,
        lastName: timesheetApprover?.lastName || undefined,
        email: timesheetApprover?.email || undefined,
        phoneNumber: timesheetApprover?.phone || undefined,
      }),
    );
  }

  return {
    bestPrimaryContacts,
    billingContacts: mappedBillingContacts,
    timesheetApproverNotApplicable:
      timesheetApproverInfo?.notApplicable || false,
    timesheetApproverSameAsSupervisor: timesheetApproverInfo.sameAsSupervisor,
    customerId: customer,
    supervisorInfo: {
      firstName: supervisorInfo.firstName,
      lastName: supervisorInfo.lastName,
      email: supervisorInfo.email,
      phoneNumber: supervisorInfo.phone,
      title: supervisorInfo.title.trim() || undefined,
    },
    timesheetApproverInfo: timesheetApprover,
    additionalTimesheetApprovers: extraTimesheetApprovers,
    timesheetBackupApproverInfo: {
      firstName: timeBackupApprover.firstName,
      lastName: timeBackupApprover.lastName,
      email: timeBackupApprover.email,
      phoneNumber: timeBackupApprover.phone || undefined,
    },
  };
};

interface ContactRequest {
  bestPrimaryContacts: AssignmentContactPersonEditModel[];
  supervisorInfo: AssignmentSupervisorInfoEditModel;
  timesheetApproverSameAsSupervisor: boolean;
  timesheetApproverInfo?: AssignmentTimesheetApproverInfoEditModel;
  timesheetBackupApproverInfo: AssignmentTimesheetBackupApproverInfoEditModel;
  timesheetApproverNotApplicable?: boolean;
  customerId?: string;
}

export const mapContactInfoToRequest = (
  values: ContactInfoFormValues,
): ContactRequest => {
  const {
    primaryContacts,
    supervisorInfo,
    timeBackupApprover,
    timesheetApprover,
  } = values;
  const { bestContacts, newPrimaryContact } = primaryContacts;
  const { supervisor, newSupervisor } = supervisorInfo;
  const { sameAsSupervisor, approver, newTimesheetApprover, notApplicable } =
    timesheetApprover;
  const bestPrimaryContacts: AssignmentContactPersonEditModel[] = bestContacts
    .filter((value) => !isOtherOptionSelected(value))
    .map((contactId) => ({
      contactId,
    }));

  const isOtherPrimaryContactSelected = bestContacts.some((value) =>
    isOtherOptionSelected(value),
  );

  if (isOtherPrimaryContactSelected) {
    bestPrimaryContacts.push({
      firstName: newPrimaryContact?.firstName || undefined,
      lastName: newPrimaryContact?.lastName || undefined,
      email: newPrimaryContact?.email || undefined,
      title: newPrimaryContact?.title || undefined,
      phoneNumber: newPrimaryContact?.phone || undefined,
    });
  }

  const supervisorInfoResult: AssignmentSupervisorInfoEditModel =
    isOtherOptionSelected(supervisor)
      ? {
          firstName: newSupervisor?.firstName,
          lastName: newSupervisor?.lastName,
          email: newSupervisor?.email,
          title: newSupervisor?.title,
          phoneNumber: newSupervisor?.phone,
        }
      : { supervisorId: supervisor };

  let timesheetApproverInfo:
    | AssignmentTimesheetApproverInfoEditModel
    | undefined;

  if (!sameAsSupervisor && !notApplicable) {
    if (isOtherOptionSelected(approver)) {
      timesheetApproverInfo = {
        firstName: newTimesheetApprover?.firstName || undefined,
        lastName: newTimesheetApprover?.lastName || undefined,
        email: newTimesheetApprover?.email || undefined,
        phoneNumber: newTimesheetApprover?.phone || undefined,
      };
    } else {
      timesheetApproverInfo = {
        timesheetApproverId: approver,
      };
    }
  }

  return {
    bestPrimaryContacts,
    supervisorInfo: supervisorInfoResult,
    timesheetApproverSameAsSupervisor: sameAsSupervisor,
    timesheetApproverInfo,
    timesheetBackupApproverInfo: {
      firstName: timeBackupApprover.firstName,
      lastName: timeBackupApprover.lastName,
      email: timeBackupApprover.email,
      phoneNumber: timeBackupApprover.phone || undefined,
    },
    timesheetApproverNotApplicable: timesheetApprover?.notApplicable,
  };
};

interface WorkAndBillingLocationRequest {
  workSameAsHomeOrBusinessLocation: boolean;
  workLocation?: LocationModel;
  billingSameAsWorkLocation: boolean;
  billingLocation?: LocationModel;
  billingEmail?: string;
}
// there might be used employee location
export const mapWorkAndBillingLocationToRequest = (
  worksiteLocationValues: WorksiteLocationFormValues,
): WorkAndBillingLocationRequest => {
  const { work, billing } = worksiteLocationValues;
  let workLocation;
  let billingLocation;

  if (!work.locationSameAsMain) {
    workLocation = {
      countryId: toNumber(work.country) || undefined,
      stateId: toNumber(work.state) || undefined,
      internationalStateName: work.internationalStateName || undefined,
      city: work.city || undefined,
      internationalCityName: work.internationalCityName || undefined,
      address: work.address || undefined,
      zipCode: work.zip || undefined,
    };
  }

  if (!billing.locationSameAsWork) {
    billingLocation = {
      countryId: toNumber(billing.country) || undefined,
      stateId: toNumber(billing.state) || undefined,
      internationalStateName: billing.internationalStateName || undefined,
      city: billing.city || undefined,
      internationalCityName: billing.internationalCityName || undefined,
      address: billing.address || undefined,
      zipCode: billing.zip || undefined,
    };
  }

  return {
    workSameAsHomeOrBusinessLocation: work.locationSameAsMain,
    billingSameAsWorkLocation: billing.locationSameAsWork,
    workLocation,
    billingLocation,
    billingEmail: billing.email || undefined,
  };
};

export const mapWorkerInformationToRequest = (
  workerInfo: WorkerInfoFormValues,
  assignmentType: AssignmentTypeEnum,
  isDraft: boolean = false,
): AssignmentWorkerInfoEditModel => {
  const { worker, taxId, workerFileKey } = workerInfo;
  const {
    workerId,
    firstName,
    lastName,
    email,
    phone,
    state,
    city,
    address,
    zip,
    workerType,
    country,
    internationalCityName,
    internationalStateName,
  } = worker;
  const isAddWorker = isAddWorkerSelected(workerId);
  let workerInformation: AssignmentWorkerInfoEditModel;

  if (isAddWorker) {
    workerInformation = {
      workerInfo: {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        phoneNumber: phone || undefined,
        taxId:
          assignmentType === AssignmentTypeEnum.ICAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.ICAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      },
      location: {
        stateId: toNumber(state) || undefined,
        city: city || undefined,
        address: address || undefined,
        zipCode: zip || undefined,
        countryId: country || undefined,
        internationalStateName: internationalStateName || undefined,
        internationalCityName: internationalCityName || undefined,
      },
      workerType,
    };
  } else {
    workerInformation = {
      workerId: workerId || undefined,
    };

    // Include workerType only for draft and if worker is not specified
    if (isDraft && !workerId) {
      workerInformation.workerType = workerType;
    }

    if (
      assignmentType === AssignmentTypeEnum.ICAssignment &&
      (!isNil(taxId) || workerFileKey.length)
    ) {
      workerInformation.workerInfo = {
        taxId:
          assignmentType === AssignmentTypeEnum.ICAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.ICAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      };
    }
  }

  return workerInformation;
};

export const mapContractorCompanyInformationToRequest = (
  workerInfo: WorkerInfoFormValues,
  assignmentType: AssignmentTypeEnum,
): AssignmentContractorCompanyInfoEditModel => {
  const { company, taxId, workerFileKey } = workerInfo;
  const {
    companyId,
    companyName,
    firstName,
    lastName,
    email,
    phone,
    state,
    city,
    address,
    zip,
    country,
    internationalCityName,
    internationalStateName,
  } = company;

  const isAddCompany = isAddCompanySelected(companyId);
  let contractorCompanyInfo: AssignmentContractorCompanyInfoEditModel;

  if (isAddCompany) {
    contractorCompanyInfo = {
      companyName: companyName || undefined,
      representativeContact: {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: email || undefined,
        phoneNumber: phone || undefined,
        taxId:
          assignmentType === AssignmentTypeEnum.SCAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.SCAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      },
      location: {
        countryId: country || undefined,
        stateId: toNumber(state) || undefined,
        internationalStateName: internationalStateName || undefined,
        city: city || undefined,
        internationalCityName: internationalCityName || undefined,
        address: address || undefined,
        zipCode: zip || undefined,
      },
    };
  } else {
    contractorCompanyInfo = {
      companyId: companyId || undefined,
    };

    if (
      assignmentType === AssignmentTypeEnum.SCAssignment &&
      (!isNil(taxId) || workerFileKey.length)
    ) {
      contractorCompanyInfo.representativeContact = {
        taxId:
          assignmentType === AssignmentTypeEnum.SCAssignment && !isNil(taxId)
            ? taxId
            : undefined,
        workerFileKey:
          assignmentType === AssignmentTypeEnum.SCAssignment &&
          workerFileKey.length
            ? workerFileKey[0]
            : undefined,
      };
    }
  }

  return contractorCompanyInfo;
};

export const mapInternalReportsToRequest = (
  internalReports: AssignmentTeammateSelectModel[],
): AssignmentTeammatesEditModel[] => {
  return internalReports
    .map((internalReport) => {
      return {
        ...internalReport,
        userId:
          internalReport.teammateType === AssignmentTeammateTypeEnum.Teammate
            ? internalReport.userId
            : undefined,
        pendingTeammateEmail:
          internalReport.teammateType ===
          AssignmentTeammateTypeEnum.PendingTeammate
            ? internalReport.userId
            : undefined,
      };
    })
    .filter((internalReport) =>
      internalReport.teammateType === AssignmentTeammateTypeEnum.Teammate &&
      !internalReport.userId
        ? false
        : true,
    );
};

export const mapEffectiveDateToInitialValues = (
  oldFinancialInfoValues?: FinancialInfoFormValues,
  newFinancialInfoValues?: FinancialInfoFormValues,
): FinancialSchedulerFormValues | undefined => {
  const financialInfoScheduler: FinancialInfoSchedulerModel = {};
  const multiRateSchedulers: MultiRateSchedulerEditModel[] = [];

  const hasPayRateChanged = !isEqual(
    oldFinancialInfoValues?.payRate || '',
    newFinancialInfoValues?.payRate || '',
  );

  const hasBillRateChanged = !isEqual(
    oldFinancialInfoValues?.billRate || '',
    newFinancialInfoValues?.billRate || '',
  );

  if (hasPayRateChanged || hasBillRateChanged) {
    financialInfoScheduler.payRate = !isNil(newFinancialInfoValues?.payRate)
      ? newFinancialInfoValues?.payRate
      : undefined;
    financialInfoScheduler.billRate = !isNil(newFinancialInfoValues?.billRate)
      ? newFinancialInfoValues?.billRate
      : undefined;
    financialInfoScheduler.profitRate = !isNil(
      newFinancialInfoValues?.profitRate,
    )
      ? newFinancialInfoValues?.profitRate
      : undefined;
    financialInfoScheduler.mbpRate = !isNil(newFinancialInfoValues?.mbpRate)
      ? newFinancialInfoValues?.mbpRate
      : undefined;
  }

  const hasOvertimeChanged = !isEqual(
    oldFinancialInfoValues?.overtime || '',
    newFinancialInfoValues?.overtime || '',
  );

  if (hasOvertimeChanged) {
    financialInfoScheduler.overtime = !isNil(newFinancialInfoValues?.overtime)
      ? newFinancialInfoValues?.overtime
      : undefined;
  }

  const isResetOvertime =
    hasOvertimeChanged &&
    newFinancialInfoValues?.overtime === OvertimeEnum.Exempt;

  const hasOvertimeBillRateChanged = !isEqual(
    !isNil(oldFinancialInfoValues?.overtimeBillRate)
      ? oldFinancialInfoValues?.overtimeBillRate
      : '',
    !isNil(newFinancialInfoValues?.overtimeBillRate)
      ? newFinancialInfoValues?.overtimeBillRate
      : '',
  );

  if (hasOvertimeBillRateChanged) {
    financialInfoScheduler.overtimeBillRate = !isNil(
      newFinancialInfoValues?.overtimeBillRate,
    )
      ? newFinancialInfoValues?.overtimeBillRate
      : isResetOvertime
      ? 0
      : undefined;
  }

  const hasOvertimePayRateChanged = !isEqual(
    !isNil(oldFinancialInfoValues?.overtimePayRate)
      ? oldFinancialInfoValues?.overtimePayRate
      : '',
    !isNil(newFinancialInfoValues?.overtimePayRate)
      ? newFinancialInfoValues?.overtimePayRate
      : '',
  );

  if (hasOvertimePayRateChanged) {
    financialInfoScheduler.overtimePayRate = !isNil(
      newFinancialInfoValues?.overtimePayRate,
    )
      ? newFinancialInfoValues?.overtimePayRate
      : isResetOvertime
      ? 0
      : undefined;
  }

  const hasDTBillRateChanged = !isEqual(
    !isNil(oldFinancialInfoValues?.dtBillRate)
      ? oldFinancialInfoValues?.dtBillRate
      : '',
    !isNil(newFinancialInfoValues?.dtBillRate)
      ? newFinancialInfoValues?.dtBillRate
      : '',
  );

  if (hasDTBillRateChanged) {
    financialInfoScheduler.dtBillRate = !isNil(
      newFinancialInfoValues?.dtBillRate,
    )
      ? newFinancialInfoValues?.dtBillRate
      : isResetOvertime
      ? 0
      : undefined;
  }

  const hasMultiRatesChanged = !isEqual(
    oldFinancialInfoValues?.multiRates || '',
    newFinancialInfoValues?.multiRates || '',
  );

  if (hasMultiRatesChanged) {
    const { oldData: oldMultiRates, newData: newMultiRates } =
      convertArraysToOneLength<MultiRateSchedulerEditModel>(
        oldFinancialInfoValues?.multiRates || [],
        newFinancialInfoValues?.multiRates || [],
        {
          index: undefined,
          payRate: undefined,
          billRate: undefined,
        },
      );
    oldMultiRates.forEach((multiRate: MultiRateSchedulerEditModel, index) => {
      const multiRateScheduler: MultiRateSchedulerEditModel = {};

      const hasPayRateChanged = !isEqual(
        multiRate.payRate,
        newMultiRates[index].payRate,
      );

      if (hasPayRateChanged && !isNil(newMultiRates[index].payRate)) {
        multiRateScheduler.payRate = newMultiRates[index].payRate;
      }

      const hasBillRateChanged = !isEqual(
        multiRate.billRate,
        newMultiRates[index].billRate,
      );

      if (hasBillRateChanged && !isNil(newMultiRates[index].billRate)) {
        multiRateScheduler.billRate = newMultiRates[index].billRate;
      }

      if (
        (hasBillRateChanged && !isNil(newMultiRates[index].billRate)) ||
        (hasPayRateChanged && !isNil(newMultiRates[index].payRate))
      ) {
        multiRateSchedulers.push({
          ...multiRateScheduler,
          index: index,
        });
      }
    });
  }

  if (!isEmpty(multiRateSchedulers) || !isEmpty(financialInfoScheduler))
    return {
      multiRateSchedulers: multiRateSchedulers,
      financialInfoScheduler: !isEmpty(financialInfoScheduler)
        ? financialInfoScheduler
        : undefined,
      effectiveDate: undefined,
    };
};

export const mapFinancialInfoToRequest = (
  values: AssignmentInitialValues,
  financialSchedulerFormValues?: FinancialSchedulerFormValues,
  predefinedInitialValues?: AssignmentInitialValues,
) => {
  const financialInfoInitialValues = predefinedInitialValues?.financialInfo;

  const { financialInfo: financialInfoFormValues } = values;

  const multiRateSchedulers =
    financialSchedulerFormValues?.multiRateSchedulers || [];

  const multiRates = (financialInfoFormValues?.multiRates || []).map(
    (multiRate, index) => {
      const multiRateScheduler = multiRateSchedulers.find(
        (multiRateScheduler) => multiRateScheduler.index === index,
      );

      if (!multiRateScheduler) return multiRate;

      const oldBillRateValue = multiRateScheduler.hasOwnProperty('billRate')
        ? (financialInfoInitialValues?.multiRates || [])[index]?.billRate
        : multiRate?.billRate;

      const oldPayRateValue = multiRateScheduler.hasOwnProperty('payRate')
        ? (financialInfoInitialValues?.multiRates || [])[index]?.payRate
        : multiRate?.payRate;

      return {
        ...multiRate,
        billRate: oldBillRateValue,
        payRate: oldPayRateValue,
      };
    },
  );

  return {
    payRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'payRate',
      )
        ? financialInfoInitialValues?.payRate
        : financialInfoFormValues?.payRate,
    billRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'billRate',
      )
        ? financialInfoInitialValues?.billRate
        : financialInfoFormValues?.billRate,
    dtBillRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'dtBillRate',
      )
        ? financialInfoInitialValues?.dtBillRate
        : financialInfoFormValues?.dtBillRate,
    profitRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'profitRate',
      )
        ? financialInfoInitialValues?.profitRate
        : financialInfoFormValues?.profitRate,
    overtimeBillRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtimeBillRate',
      )
        ? financialInfoInitialValues?.overtimeBillRate
        : financialInfoFormValues?.overtimeBillRate,
    overtimePayRate:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtimePayRate',
      )
        ? financialInfoInitialValues?.overtimePayRate
        : financialInfoFormValues?.overtimePayRate,

    payType: financialInfoFormValues?.payType,
    overtime:
      financialSchedulerFormValues?.financialInfoScheduler?.hasOwnProperty(
        'overtime',
      )
        ? financialInfoInitialValues?.overtime
        : financialInfoFormValues?.overtime,
    mbpRate: financialInfoFormValues?.mbpRate,
    dtPayRate: financialInfoFormValues?.dtPayRate,
    multiRates: multiRates,
  };
};

export const mapFinancialSchedulerToRequest = (
  value: FinancialSchedulerFormValues,
): AssignmentFinancialSchedulerEditModel => {
  if (value.financialInfoScheduler?.overtime === OvertimeEnum.Exempt) {
    value.financialInfoScheduler = {
      ...value.financialInfoScheduler,
      overtimeBillRate: undefined,
      overtimePayRate: undefined,
      dtBillRate: undefined,
    };
  }

  return {
    effectiveDate: !isNil(value.effectiveDate)
      ? getDateWithoutTime(value.effectiveDate)
      : undefined,
    financialInfoScheduler: !isEmpty(value.financialInfoScheduler)
      ? value.financialInfoScheduler
      : undefined,
    multiRateSchedulers: value.multiRateSchedulers,
  };
};
